import React, { useEffect, useMemo, useRef, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AlertCircle, FolderTree as FolderTreeIcon, Loader2, Tags } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { FranklinLogo } from "@/components/ui/franklin-logo";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { organizeService } from "../../services/organizeService";
import {
  isScopeAvailable,
  organizeStartConflict,
  reviewableMoves,
  TAG_MODE_MAX_FOLDERS,
} from "../../types/organize";
import type {
  OrganizeDimension,
  OrganizeMode,
  OrganizeScope,
  OrganizeStartConflict,
} from "../../types/organize";
import { errorMessage } from "@/utils/postgrestErrors";
import type { DocumentTranslations } from "@/lib/i18n/types/documents";
import { useFolderOrganize } from "../../hooks/useFolderOrganize";
import { OrganizeProposalReview } from "./OrganizeProposalReview";
import { DocumentViewerDialog } from "../DocumentViewerDialog";
import { useDocumentViewer } from "../../hooks/useDocumentViewer";
import { OrganizeTagPicker } from "./OrganizeTagPicker";
import { OrganizeTargetHeader } from "./OrganizeTargetHeader";

interface OrganizeFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buildingId: string;
  projectId: string;
  /** Folder to organise; null targets the PROJECT ROOT (loose files). */
  folderId: number | null;
  /** Folder name, or the project name for a root run — shown in the title. */
  folderName: string;
  /** Project the target sits in; named in the header so runs are unambiguous. */
  projectName: string;
  /** Ancestors of the target folder, target last. Empty for a root run. */
  folderPath: string[];
  /** Called after a proposal is applied so the view refreshes its documents. */
  onApplied?: () => void;
}

/** The copy for each way a run can overlap one already in progress. */
const START_CONFLICT_COPY: Record<OrganizeStartConflict, keyof DocumentTranslations["organize"]> = {
  sameFolder: "alreadyRunning",
  wholeProject: "alreadyRunningWholeProject",
  parentFolder: "alreadyRunningParent",
  subfolder: "alreadyRunningSubfolder",
  folderInProject: "alreadyRunningProject",
};

function interpolate(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce(
    (acc, [key, value]) => acc.replace(`{${key}}`, String(value)),
    template
  );
}

/**
 * "Organize with AI" (WHA-3654) — launch, progress, and review in one dialog,
 * staged by the request row's status. The agent only proposes; documents move
 * when the user applies the proposal here.
 */
export function OrganizeFolderDialog({
  open,
  onOpenChange,
  buildingId,
  projectId,
  folderId,
  folderName,
  projectName,
  folderPath,
  onApplied,
}: OrganizeFolderDialogProps) {
  const { t, language } = useLanguage();
  const [scope, setScope] = useState<OrganizeScope>("loose");
  const [mode, setMode] = useState<OrganizeMode>("agent");
  const [dimensions, setDimensions] = useState<OrganizeDimension[]>(["category", "discipline"]);
  const [instructions, setInstructions] = useState("");
  const [excludedIds, setExcludedIds] = useState<Set<number>>(new Set());
  // documentId -> the proposed folder key the reviewer dragged it onto. Held
  // here until Apply, exactly like the renames: nothing is written, so a
  // correction costs no round trip and cannot race the proposal's expiry.
  const [assignmentOverrides, setAssignmentOverrides] = useState<Map<number, string>>(new Map());
  const viewer = useDocumentViewer();
  const contentRef = useRef<HTMLDivElement>(null);

  const {
    request,
    isLoading,
    isError,
    refetchRequest,
    start,
    isStarting,
    cancel,
    isCancelling,
    apply,
    isApplying,
  } = useFolderOrganize({ buildingId, projectId, folderId, enabled: open, onApplied });

  const numericProjectId = Number(projectId);
  // A run already in flight (or awaiting review) means the launch screen is not
  // showing, so the preview must not fetch behind it.
  const showReviewGate =
    !!request &&
    request.status !== "cancelled" &&
    ["pending", "running", "proposal_ready", "applying"].includes(request.status);

  const countsQuery = useQuery({
    queryKey: ["folder-organize-counts", projectId, folderId ?? "root"],
    queryFn: () =>
      Number.isNaN(numericProjectId)
        ? Promise.resolve(null)
        : organizeService.getScopeCounts(folderId, numericProjectId),
    enabled: open && !Number.isNaN(numericProjectId),
  });

  // The structure tag mode would produce, redrawn as the picker changes. Keyed
  // on the ORDERED dimension list, so reordering is a different cache entry.
  const tagPreviewQuery = useQuery({
    queryKey: [
      "folder-organize-tag-preview",
      projectId,
      folderId ?? "root",
      scope,
      dimensions.join(">"),
      language,
    ],
    queryFn: () =>
      organizeService.previewTagOrganization(
        folderId,
        numericProjectId,
        scope,
        dimensions,
        language
      ),
    enabled:
      open &&
      mode === "tags" &&
      dimensions.length > 0 &&
      !Number.isNaN(numericProjectId) &&
      !showReviewGate,
  });

  const tagFolderCount = tagPreviewQuery.data?.counts.folders ?? 0;
  const tagMoveCount = tagPreviewQuery.data?.counts.moves ?? 0;
  const tagCapExceeded = tagFolderCount > TAG_MODE_MAX_FOLDERS;

  const proposalDocIds = useMemo(
    () => request?.proposal?.assignments.map((a) => a.document_id) ?? [],
    [request]
  );
  // Keyed on the document set, not just the request id: a proposal rewritten
  // under the same row (realtime delivers it in place) would otherwise reuse
  // the cached map and render every newly-referenced document as "#id".
  //
  // That key change must not throw the review away, though: without previous
  // data the tree unmounts behind the loader and takes its local state with it
  // — collapsed folders, and any rename the reviewer is mid-way through typing.
  // Keeping the last map means only the FIRST load shows the loader; a rewrite
  // re-renders in place, with newly-referenced files reading "#id" for the one
  // fetch it takes to name them.
  const namesQuery = useQuery({
    queryKey: ["folder-organize-doc-names", request?.id, proposalDocIds],
    queryFn: () => organizeService.getProposalDocuments(proposalDocIds),
    enabled: open && request?.status === "proposal_ready" && proposalDocIds.length > 0,
    placeholderData: keepPreviousData,
  });

  // A fresh proposal starts with every move selected.
  useEffect(() => {
    setExcludedIds(new Set());
    setAssignmentOverrides(new Map());
  }, [request?.id, request?.status]);

  /**
   * Both modes fail the same way, because both RPCs share the conflict rules —
   * so this reports the reason rather than collapsing every failure into one
   * sentence. An unexplained "Could not start the analysis" cost a real
   * debugging session: the server was saying "already in progress for a folder
   * of this project", which the old copy could not express.
   */
  const reportStartError = (error: unknown) => {
    const message = errorMessage(error);
    const conflict = organizeStartConflict(message);
    if (conflict) {
      toast.error(t.documents.organize[START_CONFLICT_COPY[conflict]]);
      return;
    }
    // Anything unrecognised keeps the friendly headline but carries the
    // server's own words, so the next report is self-diagnosing.
    toast.error(t.documents.organize.startError, { description: message });
  };

  const handleStart = async () => {
    try {
      await start(
        mode === "tags"
          ? { mode: "tags", scope, dimensions, language }
          : { mode: "agent", scope, instructions }
      );
    } catch (error) {
      reportStartError(error);
    }
  };

  // Retry after a failed/expired run repeats that run's actual parameters —
  // the launch screen's local scope/instructions state was never shown.
  const handleRetry = async () => {
    try {
      // Repeat the run's ACTUAL parameters — the launch screen's local state
      // was never shown for a resumed failure.
      await start(
        request?.mode === "tags" && request.dimensions?.length
          ? {
              mode: "tags",
              scope: request.scope,
              dimensions: request.dimensions,
              language: request.language ?? language,
            }
          : {
              mode: "agent",
              scope: request?.scope ?? scope,
              instructions: request?.instructions ?? instructions,
            }
      );
    } catch (error) {
      reportStartError(error);
    }
  };

  const handleApply = async (folderNameOverrides: Record<string, string>) => {
    try {
      // Malformed moves (missing/unknown target_key) never render a checkbox
      // in the review, so exclude them explicitly — only what the user saw
      // and left checked gets applied.
      const reviewableIds = new Set(
        reviewableMoves(request?.proposal ?? null).map((a) => a.document_id)
      );
      const hiddenMoveIds = (request?.proposal?.assignments ?? [])
        .filter((a) => a.action === "move" && !reviewableIds.has(a.document_id))
        .map((a) => a.document_id);
      const result = await apply({
        excludedDocumentIds: [...new Set([...excludedIds, ...hiddenMoveIds])],
        folderNameOverrides,
        // An excluded file is not moved at all, so a drag it also carries is
        // meaningless — and sending one would make the server refuse an apply
        // the reviewer sees no problem with.
        assignmentOverrides: Object.fromEntries(
          [...assignmentOverrides].filter(([id]) => !excludedIds.has(id))
        ),
      });
      if (result.status === "expired") {
        toast.error(t.documents.organize.expiredError);
        return;
      }
      if (result.status !== "applied") {
        toast.error(result.error ?? t.documents.organize.applyError);
        return;
      }
      toast.success(
        interpolate(t.documents.organize.appliedToast, {
          moved: result.moved ?? 0,
          folders: result.folders_created ?? 0,
        })
      );
      if ((result.skipped_stale ?? 0) > 0) {
        toast.warning(
          interpolate(t.documents.organize.skippedStaleWarning, {
            count: result.skipped_stale ?? 0,
          })
        );
      }
      onOpenChange(false);
    } catch {
      toast.error(t.documents.organize.applyError);
    }
  };

  const handleDiscard = async () => {
    try {
      await cancel();
      onOpenChange(false);
    } catch {
      toast.error(t.documents.organize.cancelError);
    }
  };

  const dialogTitle = `${t.documents.organize.titleProject} ${projectName}${
    folderName ? ` › ${folderName}` : ""
  }`;

  const status = request?.status;
  // Until the request query resolves there is no `status`, so every stage below
  // is false and the dialog renders its header over an empty box — on EVERY
  // open, not just when a proposal exists. This stage is what fills it.
  const showLoading = isLoading;
  const showReview = status === "proposal_ready";
  const showProgress = status === "pending" || status === "running" || status === "applying";
  const showFailed = status === "failed" || status === "expired";
  // Request fetch failed → we don't know whether a run/proposal exists, so
  // never fall through to the launch screen (a duplicate Start is costly).
  const showRequestError = !isLoading && isError;
  const showLaunch =
    !showLoading && !showRequestError && !showReview && !showProgress && !showFailed;

  // Only the scopes that can actually be picked are offered. A greyed-out
  // "Whole folder tree" on a folder with no subfolders is an option that can
  // never become available — it reads as something the user is failing to
  // unlock. While the counts are still loading nothing is filtered, so options
  // do not pop in and out as the query resolves.
  const countsReady = !!countsQuery.data;
  const allScopeOptions: Array<{
    value: OrganizeScope;
    label: string;
    hint: string;
    available: boolean;
  }> = [
    {
      value: "loose",
      label: t.documents.organize.scopeLoose,
      hint: interpolate(t.documents.organize.scopeLooseHint, {
        count: countsQuery.data?.looseDocuments ?? "…",
      }),
      available: isScopeAvailable("loose", countsQuery.data ?? undefined),
    },
    {
      value: "subtree",
      label: t.documents.organize.scopeSubtree,
      hint: interpolate(t.documents.organize.scopeSubtreeHint, {
        count: countsQuery.data?.subtreeDocuments ?? "…",
        folders: countsQuery.data?.subfolders ?? "…",
      }),
      available: isScopeAvailable("subtree", countsQuery.data ?? undefined),
    },
  ];

  const scopeOptions = countsReady ? allScopeOptions.filter((o) => o.available) : allScopeOptions;

  // Shared dialog instance — reset the preselection on each open so a
  // previous folder's auto-switch to "subtree" doesn't leak into this one.
  useEffect(() => {
    if (open) {
      setScope("loose");
      setMode("agent");
      setDimensions(["category", "discipline"]);
      setInstructions("");
    }
  }, [open, folderId]);

  // Loose is preselected, but only offered scopes can be selected — otherwise
  // filtering the list out from under the selection would leave Start pointing
  // at a scope with nothing in it and no visible way to change it.
  useEffect(() => {
    if (!open || !countsReady) return;
    if (!scopeOptions.some((o) => o.value === scope) && scopeOptions.length > 0) {
      setScope(scopeOptions[0].value);
    }
  }, [open, countsReady, scopeOptions, scope]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // overflow-hidden completes the flex chain: without it the content
        // grows past max-h and the review list never gets a scrollbar.
        className={cn(
          // min-w-0: DialogContent is a grid, whose items default to
          // min-width:auto — without this a deep tree row widens the track past
          // max-w-* and the modal spills off-screen instead of scrolling.
          "flex min-w-0 flex-col",
          // Wider throughout: the header now carries a folder path, and the
          // launch screen carries a live structure preview.
          showReview ? "h-[85vh] overflow-hidden sm:max-w-4xl" : "sm:max-w-2xl"
        )}
        ref={contentRef}
        // The info-icon tooltip trigger is the first focusable element, and
        // Radix tooltips open on focus — letting the dialog autofocus it made
        // the tooltip pop the moment the modal opened. Focus the content
        // container instead (Radix gives it tabIndex -1); the icon still shows
        // its tooltip on hover/tab. Preventing the default WITHOUT focusing
        // anything would leave focus on the trigger behind the modal, so Tab
        // would walk the page underneath.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          contentRef.current?.focus();
        }}
      >
        <DialogHeader>
          <OrganizeTargetHeader projectName={projectName} folderPath={folderPath} />
        </DialogHeader>

        {showLoading && (
          // role="status" so the wait is announced, not just drawn. The text
          // below is the accessible name, which is why the mark stays alt="".
          <div role="status" className="flex flex-col items-center justify-center gap-3 py-12">
            <FranklinLogo
              size="lg"
              showBorder={false}
              alt=""
              imageClassName="animate-spin motion-reduce:animate-none"
            />
            <p className="text-center text-sm text-muted-foreground">
              {t.documents.organize.loading}
            </p>
          </div>
        )}

        {showLaunch && (
          <div className="min-w-0 space-y-4">
            <div
              className="grid grid-cols-2 gap-2"
              role="radiogroup"
              aria-label={t.documents.organize.groupBy}
            >
              {[
                {
                  value: "agent" as const,
                  label: t.documents.organize.modeAgent,
                  hint: t.documents.organize.modeAgentHint,
                },
                {
                  value: "tags" as const,
                  label: t.documents.organize.modeTags,
                  hint: t.documents.organize.modeTagsHint,
                },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={mode === option.value}
                  onClick={() => setMode(option.value)}
                  className={cn(
                    "rounded-lg border p-3 text-left transition-colors",
                    mode === option.value
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-muted/50"
                  )}
                >
                  <span className="flex items-center gap-2 text-sm font-medium">
                    {option.value === "agent" ? (
                      // The mark, not a generic sparkle: this option IS Franklin,
                      // and the same mark is what the progress screen shows while
                      // he works. alt="" because the label names him already.
                      <FranklinLogo
                        size="xs"
                        showBorder={false}
                        alt=""
                        className="h-4 w-4 shrink-0"
                      />
                    ) : (
                      <Tags className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
                    )}
                    {option.label}
                  </span>
                  <span className="mt-1 block text-xs text-muted-foreground">{option.hint}</span>
                </button>
              ))}
            </div>

            {mode === "agent" ? (
              <div className="space-y-1.5">
                <label
                  htmlFor="organize-instructions"
                  className="text-sm font-medium text-foreground"
                >
                  {t.documents.organize.additionalInstructions}
                </label>
                <Textarea
                  id="organize-instructions"
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder={t.documents.organize.additionalInstructionsPlaceholder}
                  maxLength={2000}
                  rows={2}
                  className="resize-y text-sm"
                />
              </div>
            ) : (
              <OrganizeTagPicker
                dimensions={dimensions}
                onChange={setDimensions}
                preview={tagPreviewQuery.data ?? null}
                isLoading={tagPreviewQuery.isFetching}
                isError={tagPreviewQuery.isError}
                capExceeded={tagCapExceeded}
                maxFolders={TAG_MODE_MAX_FOLDERS}
              />
            )}
            {countsQuery.isError && (
              <p className="flex items-center gap-1.5 text-xs text-destructive">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {t.documents.organize.countsError}
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={() => countsQuery.refetch()}
                >
                  {t.documents.organize.retry}
                </Button>
              </p>
            )}
            {/*
              Scope sits in the footer beside Start, as two compact toggles
              rather than the two large cards it used to be. It is a single
              either/or that the file counts already explain, so it does not
              earn the top of a modal whose subject is the structure below it;
              the full sentence stays on the button's title.
            */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={dialogTitle}>
                {scopeOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={scope === option.value}
                    title={option.hint}
                    onClick={() => setScope(option.value)}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                      scope === option.value
                        ? "border-primary bg-primary/5 text-foreground"
                        : "border-border text-muted-foreground hover:bg-muted/50"
                    )}
                  >
                    <FolderTreeIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    {option.label}
                  </button>
                ))}
              </div>
              <Button
                onClick={handleStart}
                disabled={
                  isStarting ||
                  countsQuery.isLoading ||
                  // Counts failed → scope sizes are unknown (or stale from a
                  // previous fetch); block Start until the retry succeeds.
                  countsQuery.isError ||
                  // Gate on the SELECTED scope's count — before the
                  // auto-switch effect runs, "loose" can still be selected
                  // while empty, and Start must not launch an empty run.
                  ((scope === "loose"
                    ? countsQuery.data?.looseDocuments
                    : countsQuery.data?.subtreeDocuments) ?? 1) === 0 ||
                  // Tags mode: nothing to group by, nothing to move, still
                  // computing, or a structure the server would refuse.
                  (mode === "tags" &&
                    (dimensions.length === 0 ||
                      tagPreviewQuery.isFetching ||
                      tagPreviewQuery.isError ||
                      tagMoveCount === 0 ||
                      tagCapExceeded))
                }
              >
                {isStarting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                {mode === "tags" ? t.documents.organize.startTags : t.documents.organize.start}
              </Button>
            </div>
          </div>
        )}

        {showRequestError && (
          <div className="flex flex-col items-center gap-3 py-6">
            <AlertCircle className="h-6 w-6 text-destructive" aria-hidden="true" />
            <p className="max-w-sm text-center text-sm text-muted-foreground">
              {t.documents.organize.requestError}
            </p>
            <Button size="sm" variant="outline" onClick={() => refetchRequest()}>
              {t.documents.organize.retry}
            </Button>
          </div>
        )}

        {showProgress && (
          <div className="flex flex-col items-center gap-3 py-6">
            {/* Same animated Franklin the chatbot shows while thinking. */}
            <FranklinLogo size="lg" showBorder={false} animated />
            <p className="text-center text-sm text-muted-foreground">
              {status === "pending"
                ? t.documents.organize.queued
                : status === "applying"
                  ? t.documents.organize.applying
                  : t.documents.organize.analyzing}
            </p>
            {(status === "pending" || status === "running") && (
              <p className="max-w-xs text-center text-xs text-muted-foreground">
                {t.documents.organize.closeHint}
              </p>
            )}
            {status === "pending" && (
              <Button variant="outline" size="sm" onClick={handleDiscard} disabled={isCancelling}>
                {t.documents.organize.cancel}
              </Button>
            )}
          </div>
        )}

        {showFailed && (
          <div className="flex flex-col items-center gap-3 py-6">
            <AlertCircle className="h-6 w-6 text-destructive" aria-hidden="true" />
            <p className="text-sm font-medium">
              {status === "expired"
                ? t.documents.organize.expiredError
                : t.documents.organize.failedTitle}
            </p>
            {request?.error && status === "failed" && (
              <p className="max-w-sm text-center text-xs text-muted-foreground">{request.error}</p>
            )}
            <Button size="sm" onClick={handleRetry} disabled={isStarting}>
              {isStarting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {t.documents.organize.retry}
            </Button>
          </div>
        )}

        {showReview && namesQuery.isError && (
          <p className="flex items-center gap-1.5 text-xs text-destructive">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {t.documents.organize.namesError}
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              onClick={() => namesQuery.refetch()}
            >
              {t.documents.organize.retry}
            </Button>
          </p>
        )}
        {showReview && request && (
          <OrganizeProposalReview
            request={request}
            documents={namesQuery.data ?? new Map()}
            isLoadingDocuments={namesQuery.isLoading}
            excludedIds={excludedIds}
            onToggleDocument={(documentId, excluded) =>
              setExcludedIds((prev) => {
                const next = new Set(prev);
                if (excluded) next.add(documentId);
                else next.delete(documentId);
                return next;
              })
            }
            assignmentOverrides={assignmentOverrides}
            onMoveDocument={(documentId, targetFolderKey) =>
              setAssignmentOverrides((prev) => {
                const next = new Map(prev);
                next.set(documentId, targetFolderKey);
                return next;
              })
            }
            onPreviewDocument={(doc) =>
              viewer.openViewer({
                id: doc.id,
                filename: doc.filename,
                file_path: doc.file_path,
                building_id: doc.building_id,
                content_type: doc.content_type,
                size: doc.size,
                created_at: doc.created_at,
                version: doc.version,
                parent_document_id: doc.parent_document_id,
              })
            }
            onApply={handleApply}
            onDiscard={handleDiscard}
            isApplying={isApplying}
          />
        )}
      </DialogContent>

      {/*
        The reviewer's eye icon opens the app's ordinary viewer OVER this
        dialog rather than a second preview built for this screen. It sits
        outside DialogContent and above it (z-[60], the same layer the protocol
        preview uses) so closing the file returns to the proposal instead of
        dismissing it — losing the review to a glance at one file would be the
        worst possible trade.
      */}
      <DocumentViewerDialog
        document={viewer.document}
        blobUrl={viewer.blobUrl}
        blobData={viewer.blobData}
        open={viewer.isOpen}
        onOpenChange={viewer.setIsOpen}
        originalDwgFilePath={viewer.originalDwgFilePath ?? undefined}
        contentClassName="z-[60]"
        overlayClassName="z-[60]"
      />
    </Dialog>
  );
}
